// src/pages/UserDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import { userAPI } from '../services/api';
import programAPI from '../services/programApi';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Alert,
    CircularProgress,
    Chip,
    CardMedia,
    IconButton,
    useTheme,
    Drawer,
    useMediaQuery,
    Fab
} from '@mui/material';
import {
    FitnessCenter,
    EmojiEvents,
    Restaurant,
    PlayArrow,
    Add,
    Menu as MenuIcon,
    Close,
    Article
} from '@mui/icons-material';
import UserNavbar from '../components/UserNavbar';
import SideMenu from '../components/SideMenu';
import Footer from "../components/Footer";

const UserDashboard = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const theme = useTheme();
    const { isDarkMode } = useCustomTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

    const [myPrograms, setMyPrograms] = useState([]);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        fetchUserData();
    }, []);

    const fetchUserData = async () => {
        try {
            setLoading(true);

            try {
                const myProgramsData = await programAPI.getMyPrograms();
                setMyPrograms(myProgramsData);
            } catch (err) {
                setMyPrograms([]);
            }

            if (user?.id) {
                try {
                    const userData = await userAPI.getById(user.id);
                    setUserProfile(userData);
                } catch (userError) {
                    console.log('Could not fetch user details:', userError);
                }
            }
        } catch (err) {
            console.error('Error fetching user data:', err);
            setError('Грешка при вчитување на податоците. Обидете се повторно.');
        } finally {
            setLoading(false);
        }
    };

    const menuItems = [
        {
            id: 1,
            title: 'Купи Програми',
            description: 'Прегледај и купи тренинг програми креирани од експерти за максимални резултати',
            icon: <FitnessCenter sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/buy-programs',
            color: '#ff7eb9',
            available: true,
            badge: 'Популарно'
        },
        {
            id: 2,
            title: 'Купи Диета',
            description: 'Здравствени диететски планови прилагодени за вашите фитнес цели',
            icon: <Restaurant sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/user-diets',
            color: '#4CAF50',
            available: true,
            badge: 'Препорачано'
        },
        {
            id: 3,
            title: 'Блог',
            description: 'Читај статии, совети и информации за фитнес и здравје',
            icon: <Article sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/user-blog',
            color: '#a5d8ff',
            available: true,
            badge: 'Ново'
        },
        {
            id: 4,
            title: 'Достигнувања',
            description: 'Следете ги вашите достигнувања, прогресија и фитнес милници',
            icon: <EmojiEvents sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/achievements',
            color: '#ffd166',
            available: false,
            badge: 'Наскоро'
        }
    ];

    const buyProgramCard = {
        id: 'buy-program',
        name: 'Купи Програма',
        description: 'Прегледај и купи нова тренинг програма',
        isBuyCard: true,
        color: '#ff7eb9'
    };

    const myDiets = [
        {
            id: 'buy-diet',
            name: 'Купи Диета',
            description: 'Изберете персонализиран диететски план',
            isBuyCard: true,
            color: '#4CAF50'
        }
    ];

    const handleItemClick = (item) => {
        setSelectedItem(item);
        if (isMobile) setMobileMenuOpen(false);
        if (!item.available) {
            alert('🚧 Оваа функционалност е во развој и наскоро ќе биде достапна!');
            return;
        }
        navigate(item.path);
    };

    const handleItemHover = (item) => {
        if (!isMobile) setSelectedItem(item);
    };

    // ✅ ПОПРАВЕНО - директно navigate без popup
    const handleProgramClick = (program) => {
        if (program.isBuyCard) {
            navigate('/buy-programs');
            return;
        }
        if (program.userProgram) {
            const isExpired = !program.userProgram.isActive || program.userProgram.remainingDays <= 0;
            if (isExpired) {
                alert('⚠️ Програмата е истечена. Контактирајте го администраторот.');
            } else {
                navigate(`/program/${program.id}/days`);
            }
        } else {
            navigate('/buy-programs');
        }
    };

    const handleDietClick = (diet) => {
        if (diet.id === 'buy-diet') {
            navigate('/user-diets');
        } else {
            navigate(`/diet/${diet.id}`);
        }
    };

    const getCardWidth = () => {
        if (isMobile) return { width: 220, minWidth: 220 };
        if (isTablet) return { width: 240, minWidth: 240 };
        return { width: 280, minWidth: 280 };
    };

    const userName = userProfile?.firstName && userProfile?.lastName
        ? `${userProfile.firstName} ${userProfile.lastName}`
        : user?.firstName && user?.lastName
            ? `${user.firstName} ${user.lastName}`
            : 'Корисник';

    // ===== PROGRAM CARD =====
    const ProgramCard = ({ program, onClick }) => {
        const cardDimensions = getCardWidth();
        const isExpired = program.userProgram && (!program.userProgram.isActive || program.userProgram.remainingDays <= 0);

        return (
            <Card
                onClick={() => onClick(program)}
                sx={{
                    ...cardDimensions,
                    cursor: 'pointer',
                    backgroundColor: 'background.card',
                    border: program.userProgram
                        ? `2px solid ${isExpired ? theme.palette.error.main : theme.palette.success.main}`
                        : `1px solid ${theme.palette.divider}`,
                    borderRadius: { xs: 2, md: 3 },
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    opacity: isExpired ? 0.7 : 1,
                    flexShrink: 0,
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: program.userProgram
                            ? `0 8px 25px ${isExpired ? 'rgba(244,67,54,0.3)' : 'rgba(76,175,80,0.3)'}`
                            : `0 8px 25px ${program.color}50`,
                        zIndex: 10,
                        '& .play-button': { opacity: 1 }
                    }
                }}
            >
                <CardMedia
                    component="div"
                    sx={{
                        height: isMobile ? 120 : isTablet ? 140 : 160,
                        background: program.userProgram
                            ? (isExpired
                                ? `linear-gradient(135deg, #f44336 0%, ${isDarkMode ? '#333' : '#f0f0f0'} 100%)`
                                : `linear-gradient(135deg, #4CAF50 0%, ${isDarkMode ? '#333' : '#f0f0f0'} 100%)`)
                            : `linear-gradient(135deg, ${program.color} 0%, ${isDarkMode ? '#333' : '#f0f0f0'} 100%)`,
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {program.isBuyCard ? (
                        <Add sx={{ fontSize: isMobile ? 40 : 60, color: 'white', opacity: 0.9 }} />
                    ) : (
                        <FitnessCenter sx={{ fontSize: isMobile ? 30 : 50, color: 'white', opacity: 0.8 }} />
                    )}

                    <Box className="play-button" sx={{
                        position: 'absolute', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        opacity: 0, transition: 'opacity 0.3s ease'
                    }}>
                        <IconButton sx={{
                            backgroundColor: 'rgba(255,255,255,0.9)',
                            color: '#333', width: isMobile ? 40 : 52, height: isMobile ? 40 : 52,
                            '&:hover': { backgroundColor: 'white' }
                        }}>
                            {program.isBuyCard
                                ? <Add sx={{ fontSize: isMobile ? 20 : 32 }} />
                                : <PlayArrow sx={{ fontSize: isMobile ? 20 : 32 }} />
                            }
                        </IconButton>
                    </Box>

                    {program.isBuyCard ? (
                        <Chip label="Купи" size="small" sx={{
                            position: 'absolute', top: 8, right: 8,
                            bgcolor: program.color, color: 'white', fontWeight: 'bold',
                            fontSize: isMobile ? '0.6rem' : '0.7rem'
                        }} />
                    ) : program.userProgram ? (
                        <>
                            <Chip
                                label={isExpired ? "Истечена" : "Активна"}
                                size="small"
                                sx={{
                                    position: 'absolute', top: 8, right: 8,
                                    bgcolor: isExpired ? '#f44336' : '#4CAF50',
                                    color: 'white', fontWeight: 'bold',
                                    fontSize: isMobile ? '0.6rem' : '0.7rem'
                                }}
                            />
                            {program.userProgram.remainingDays > 0 && (
                                <Chip
                                    label={`${program.userProgram.remainingDays}д`}
                                    size="small"
                                    sx={{
                                        position: 'absolute', top: 8, left: 8,
                                        bgcolor: 'rgba(255,255,255,0.9)',
                                        color: '#333', fontWeight: 'bold',
                                        fontSize: isMobile ? '0.6rem' : '0.7rem'
                                    }}
                                />
                            )}
                        </>
                    ) : null}
                </CardMedia>

                <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                    <Typography variant="subtitle1" sx={{
                        color: 'text.primary', fontWeight: 'bold', mb: 0.5,
                        fontSize: isMobile ? '0.85rem' : '0.95rem'
                    }}>
                        {program.name}
                    </Typography>
                    <Typography variant="body2" sx={{
                        color: 'text.secondary', mb: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: isMobile ? 1 : 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        fontSize: isMobile ? '0.75rem' : '0.85rem'
                    }}>
                        {program.description}
                    </Typography>

                    {program.userProgram && (
                        <Typography variant="caption" sx={{
                            display: 'block',
                            color: isExpired ? 'error.main' : 'success.main',
                            fontWeight: 'bold',
                            fontSize: isMobile ? '0.65rem' : '0.7rem'
                        }}>
                            ⏱️ {program.userProgram.remainingDays} денови преостануваат
                        </Typography>
                    )}

                    {program.isBuyCard && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                            <Chip label="Кликнете за да купите" size="small" sx={{
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                                color: 'text.secondary',
                                fontSize: isMobile ? '0.6rem' : '0.65rem', fontWeight: 'bold'
                            }} />
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    };

    // ===== NETFLIX SECTION =====
    const NetflixSection = ({ title, items, onClick }) => (
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
            <Typography variant={isMobile ? 'h5' : 'h4'} sx={{
                color: 'text.primary', fontWeight: 'bold',
                mb: { xs: 2, md: 3 },
                fontSize: isMobile ? '1.3rem' : isTablet ? '1.6rem' : '1.8rem',
                px: { xs: 2, md: 0 }
            }}>
                {title}
            </Typography>
            <Box sx={{
                display: 'flex',
                gap: { xs: 1.5, md: 2 },
                overflowX: 'auto',
                pb: 2,
                px: { xs: 2, md: 0 },
                '&::-webkit-scrollbar': { height: isMobile ? 4 : 6 },
                '&::-webkit-scrollbar-track': { bgcolor: isDarkMode ? '#333' : '#e0e0e0', borderRadius: 4 },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#ff7eb9', borderRadius: 4 }
            }}>
                {items.map((item, index) => (
                    <ProgramCard key={item.id || index} program={item} onClick={onClick} />
                ))}
            </Box>
        </Box>
    );

    if (loading) {
        return (
            <>
                <Box sx={{ position: 'sticky', top: 0, zIndex: 1000 }}><UserNavbar /></Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <CircularProgress sx={{ color: 'primary.main' }} />
                </Box>
            </>
        );
    }

    const displayPrograms = [
        ...myPrograms.map(userProgram => ({
            id: userProgram.program.id,
            name: userProgram.program.name,
            description: userProgram.program.description,
            userProgram: userProgram,
            color: '#4CAF50'
        })),
        buyProgramCard
    ];

    return (
        <>
            <Box sx={{ position: 'sticky', top: 0, zIndex: 1000 }}><UserNavbar /></Box>

            <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>

                {!isMobile && (
                    <Box sx={{
                        width: isTablet ? '280px' : '320px',
                        backgroundColor: 'background.paper',
                        borderRight: 1,
                        borderColor: 'divider',
                        flexShrink: 0,
                        position: 'sticky',
                        top: '64px',
                        height: 'calc(100vh - 64px)',
                        overflowY: 'auto',
                        zIndex: 999
                    }}>
                        <SideMenu
                            menuItems={menuItems}
                            selectedItem={selectedItem}
                            onItemClick={handleItemClick}
                            onItemHover={handleItemHover}
                            programsCount={myPrograms.length}
                            userName={userName}
                        />
                    </Box>
                )}

                {isMobile && (
                    <>
                        <Drawer
                            anchor="left"
                            open={mobileMenuOpen}
                            onClose={() => setMobileMenuOpen(false)}
                            PaperProps={{
                                sx: {
                                    width: '280px',
                                    backgroundColor: 'background.paper',
                                    borderRight: 1,
                                    borderColor: 'divider'
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                                <IconButton onClick={() => setMobileMenuOpen(false)}>
                                    <Close />
                                </IconButton>
                            </Box>
                            <SideMenu
                                menuItems={menuItems}
                                selectedItem={selectedItem}
                                onItemClick={handleItemClick}
                                onItemHover={handleItemHover}
                                programsCount={myPrograms.length}
                                userName={userName}
                            />
                        </Drawer>
                        <Fab
                            color="primary"
                            onClick={() => setMobileMenuOpen(true)}
                            sx={{ position: 'fixed', bottom: 16, left: 16, zIndex: 1000 }}
                        >
                            <MenuIcon />
                        </Fab>
                    </>
                )}

                <Box sx={{
                    flex: 1,
                    p: { xs: 1, sm: 2, md: 4 },
                    backgroundColor: 'background.default',
                    overflow: 'auto'
                }}>
                    <Box sx={{
                        mb: { xs: 4, md: 6 },
                        px: { xs: 1, md: 0 },
                        textAlign: { xs: 'center', md: 'left' }
                    }}>
                        <Typography variant={isMobile ? 'h4' : 'h2'} sx={{
                            fontWeight: 'bold', mb: 2,
                            background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: isMobile ? '1.8rem' : isTablet ? '2.5rem' : '3rem'
                        }}>
                            Фитнес Програми
                        </Typography>
                        <Typography variant={isMobile ? 'body1' : 'h6'} sx={{
                            color: 'text.secondary', fontWeight: 'bold',
                            fontSize: isMobile ? '1rem' : '1.2rem'
                        }}>
                            Вашиот личен фитнес универзум
                        </Typography>
                    </Box>

                    <NetflixSection title="💪 Мои Програми" items={displayPrograms} onClick={handleProgramClick} />
                    <NetflixSection title="🥗 Мои Диети" items={myDiets} onClick={handleDietClick} />

                    <Box sx={{ mt: { xs: 4, md: 6 }, textAlign: 'center', px: { xs: 2, md: 0 } }}>
                        <Card sx={{ maxWidth: 800, mx: 'auto', backgroundColor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 3 }}>
                            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ color: 'text.primary', fontWeight: 'bold', mb: 2, fontSize: isMobile ? '1.3rem' : '2rem' }}>
                                    🚀 Започнете денес!
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 'bold', mb: 3, fontSize: isMobile ? '0.9rem' : '1rem' }}>
                                    Изберете од нашата колекција на професионални фитнес програми
                                </Typography>
                                <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <Button variant="contained" size={isMobile ? 'medium' : 'large'} onClick={() => navigate('/buy-programs')}
                                            sx={{ bgcolor: 'primary.main', px: { xs: 3, md: 5 }, py: { xs: 1.5, md: 2 }, fontWeight: 'bold', fontSize: isMobile ? '0.9rem' : '1.1rem', '&:hover': { bgcolor: 'primary.dark', transform: 'translateY(-2px)' } }}>
                                        🏋️ Програми
                                    </Button>
                                    <Button variant="contained" size={isMobile ? 'medium' : 'large'} onClick={() => navigate('/user-diets')}
                                            sx={{ bgcolor: '#4CAF50', px: { xs: 3, md: 5 }, py: { xs: 1.5, md: 2 }, fontWeight: 'bold', fontSize: isMobile ? '0.9rem' : '1.1rem', '&:hover': { bgcolor: '#388E3C', transform: 'translateY(-2px)' } }}>
                                        🥗 Диети
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>

                    <Box sx={{ height: { xs: '100px', md: '60px' } }} />
                </Box>

                {error && (
                    <Box sx={{ position: 'fixed', bottom: { xs: 80, md: 20 }, right: { xs: 16, md: 20 }, left: { xs: 16, md: 'auto' }, zIndex: 2000, maxWidth: 400 }}>
                        <Alert severity="error" sx={{ borderRadius: 2 }}>
                            <Typography sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.8rem' : '0.875rem' }}>{error}</Typography>
                        </Alert>
                    </Box>
                )}
            </Box>

            <Footer />
        </>
    );
};

export default UserDashboard;